/**
 * DebriefInsightsCard — dashboard widget surfacing the trader's Decision
 * Quality score history (Sept 2026 rewrite, replaces the old emotion-tag
 * breakdown entirely). Obondo/Institutional-lite tiers only — the widget
 * is filtered out of the trader grid for other tiers in Dashboard.jsx;
 * this component also self-guards in case it's ever mounted directly.
 * Reads `GET /psych-journal/decision-quality/insights`.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasInstitutionalFeatures } from "@/lib/tier";
import { AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const BAND_COLOR = {
    elite: "#10b981", good: "#3b82f6", sub_optimal: "#f59e0b", gambling: "#ef4444",
};

function BandPill({ band }) {
    const color = BAND_COLOR[band.key] || "#94a3b8";
    return (
        <span
            className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: `${color}1a`, color }}
            data-testid={`debrief-band-${band.key}`}
        >
            {band.label}
        </span>
    );
}

export default function DebriefInsightsCard() {
    const { user } = useAuth() || {};
    const [data, setData]     = useState(null);
    const [error, setError]   = useState(false);
    const [loaded, setLoaded] = useState(false);
    const gated = hasInstitutionalFeatures(user);

    useEffect(() => {
        if (!gated) { setLoaded(true); return; }
        let cancelled = false;
        api.get("/psych-journal/decision-quality/insights")
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [gated]);

    if (!loaded) return null;

    if (!gated) {
        return (
            <div className="border border-border bg-card rounded-xl p-4 h-full flex flex-col items-center justify-center text-center" data-testid="debrief-insights-locked">
                <p className="text-sm text-muted-foreground">
                    Decision Quality scoring is available on Obondo / Advanced Trader tiers.
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-border bg-card rounded-xl p-4 h-full flex flex-col items-center justify-center gap-1.5 text-center" data-testid="debrief-insights-error">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <p className="text-xs text-muted-foreground">Couldn't load your Decision Quality insights right now. Try refreshing the page.</p>
            </div>
        );
    }

    if (!data || data.insufficient_data) {
        return (
            <div className="border border-border bg-card rounded-xl p-4 h-full flex flex-col justify-center" data-testid="debrief-insights-card">
                <p className="text-sm text-muted-foreground">
                    Complete your first post-trade Decision Quality Questionnaire — we'll show your score, band,
                    and trend here once you've logged one.
                </p>
            </div>
        );
    }

    const chartData = data.trend.map((t, i) => ({ i: i + 1, date: t.date.slice(5), score: t.score }));

    return (
        <div className="border border-border bg-card rounded-xl p-4 h-full overflow-y-auto" data-testid="debrief-insights-card">
            <div className="flex items-start justify-between gap-2 mb-3 p-2.5 rounded-lg bg-primary/5 border border-primary/20" data-testid="debrief-latest-score">
                <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Latest score</div>
                    <div className="text-2xl font-bold proportional-nums" data-testid="debrief-latest-score-value">{data.latest_score}<span className="text-sm text-muted-foreground">/50</span></div>
                </div>
                <div className="text-right space-y-1.5">
                    <BandPill band={data.latest_band} />
                    <p className="text-[11px] text-muted-foreground max-w-[180px] leading-snug">{data.latest_band.next_step}</p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="border border-border rounded-lg p-2.5" data-testid="debrief-avg-score">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Average score</div>
                    <div className="text-lg font-semibold proportional-nums">{data.avg_score}/50</div>
                </div>
                <div className="border border-border rounded-lg p-2.5" data-testid="debrief-entry-count">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Debriefs logged</div>
                    <div className="text-lg font-semibold proportional-nums">{data.total_entries}</div>
                </div>
            </div>

            {chartData.length > 1 && (
                <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Score trend</div>
                    <div className="h-24" data-testid="debrief-score-trend-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis domain={[10, 50]} tick={{ fontSize: 10 }} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: 11, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                                <Line type="monotone" dataKey="score" stroke="#006600" strokeWidth={2} dot={{ r: 2 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
}
